import { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/App";
import { CRMLayout } from "@/components/CRMLayout";
import BoutonArreter, { useArretAgent } from "@/components/ArretAgent";
import { toast } from "sonner";
import { ArrowLeft, Send, Loader2, FileText, Edit3, Terminal, CheckCircle2, AlertCircle, Circle, Plug, ExternalLink, Copy, X, Paperclip, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import ProjectTabs from "../components/ProjectTabs";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const QUICK = [
  "Corrige les erreurs de log",
  "Résume l'état du projet",
  "Mets à jour le README",
];

export default function ProjectChatPage() {
  const { projectId } = useParams();
  const { token } = useAuth();
  const [health, setHealth] = useState(null);
  const [events, setEvents] = useState([]);
  const [input, setInput] = useState("");
  const [running, setRunning] = useState(false);
  const arret = useArretAgent(token, `/api/projects/${projectId}/claude/stop`);
  const [sessionId, setSessionId] = useState(null);
  const [login, setLogin] = useState(null); // {step, url, login_id} | null
  const [code, setCode] = useState("");
  const [loginBusy, setLoginBusy] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const scrollRef = useRef(null);
  const fileRef = useRef(null);
  const dropRef = useRef(null);
  const uploadRef = useRef(null);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const checkHealth = (force = false) =>
    axios.get(`${API_URL}/api/claude/health${force ? "?force=1" : ""}`, auth)
      .then(({ data }) => setHealth(data))
      .catch(() => setHealth({ ok: false, detail: "Vérification impossible" }));

  const loadConversations = () =>
    axios.get(`${API_URL}/api/projects/${projectId}/conversations`, auth)
      .then(({ data }) => setConversations(data))
      .catch(() => {});

  const openConversation = async (sid) => {
    try {
      const { data } = await axios.get(`${API_URL}/api/projects/${projectId}/conversations/${sid}`, auth);
      setEvents((data.messages || []).map((m) => ({ kind: m.role === "user" ? "user" : "assistant", text: m.text })));
      setSessionId(sid);
    } catch (e) { toast.error("Conversation introuvable"); }
  };

  const newConversation = () => { setEvents([]); setSessionId(null); setAttachments([]); };

  useEffect(() => { checkHealth(); loadConversations(); const _iv = setInterval(checkHealth, 30000); return () => clearInterval(_iv); /* eslint-disable-next-line */ }, []);

  const startLogin = async () => {
    setLoginBusy(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/claude/login/start`, {}, auth);
      if (data.already) { toast.success(data.detail || "Claude est déjà connecté"); checkHealth(true); return; }
      setLogin({ step: 2, url: data.url, login_id: data.login_id });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossible de démarrer la connexion");
    } finally {
      setLoginBusy(false);
    }
  };

  const submitLogin = async () => {
    if (!code.trim()) return;
    setLoginBusy(true);
    try {
      await axios.post(`${API_URL}/api/claude/login/submit`,
        { login_id: login.login_id, code: code.trim() }, auth);
      toast.success("Claude connecté !");
      setLogin(null); setCode("");
      checkHealth(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Code invalide");
    } finally {
      setLoginBusy(false);
    }
  };

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [events]);

  const pushEvent = (e) => setEvents((prev) => [...prev, e]);

  const uploadFile = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await axios.post(`${API_URL}/api/projects/${projectId}/attachments`, fd, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      setAttachments((a) => [...a, data]);
      toast.success(`« ${data.name} » joint`);
    } catch (err) {
      toast.error("Échec de l'envoi du fichier");
    }
  };
  uploadRef.current = uploadFile;

  const onPickFile = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    for (const f of files) await uploadFile(f);
  };

  // Glisser-déposer robuste : écouteurs natifs sur toute la colonne du chat
  useEffect(() => {
    const el = dropRef.current;
    if (!el) return;
    let counter = 0;
    const onEnter = (e) => { e.preventDefault(); e.stopPropagation(); counter++; setDragOver(true); };
    const onOver = (e) => { e.preventDefault(); e.stopPropagation(); if (e.dataTransfer) e.dataTransfer.dropEffect = "copy"; };
    const onLeave = (e) => { e.preventDefault(); e.stopPropagation(); counter = Math.max(0, counter - 1); if (counter === 0) setDragOver(false); };
    const onDrop = async (e) => {
      e.preventDefault(); e.stopPropagation();
      counter = 0; setDragOver(false);
      const files = Array.from(e.dataTransfer?.files || []);
      for (const f of files) { if (uploadRef.current) await uploadRef.current(f); }
    };
    el.addEventListener("dragenter", onEnter);
    el.addEventListener("dragover", onOver);
    el.addEventListener("dragleave", onLeave);
    el.addEventListener("drop", onDrop);
    // empêche le navigateur d'ouvrir un fichier lâché hors zone
    const prevent = (e) => e.preventDefault();
    window.addEventListener("dragover", prevent);
    window.addEventListener("drop", prevent);
    return () => {
      el.removeEventListener("dragenter", onEnter);
      el.removeEventListener("dragover", onOver);
      el.removeEventListener("dragleave", onLeave);
      el.removeEventListener("drop", onDrop);
      window.removeEventListener("dragover", prevent);
      window.removeEventListener("drop", prevent);
    };
  }, []);

  const run = async (prompt) => {
    if ((!prompt.trim() && attachments.length === 0) || running) return;
    setRunning(true);
    let finalPrompt = prompt;
    if (attachments.length) {
      const refs = attachments.map((a) => `- ${a.path}`).join("\n");
      finalPrompt = `${prompt}\n\nFichiers joints (dans le projet, lis-les) :\n${refs}`;
      pushEvent({ kind: "user", text: `${prompt}${prompt ? "\n" : ""}📎 ${attachments.map((a) => a.name).join(", ")}` });
    } else {
      pushEvent({ kind: "user", text: prompt });
    }
    setInput("");
    setAttachments([]);
    try {
      const res = await fetch(`${API_URL}/api/projects/${projectId}/claude/run`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        ...(() => { const { signal, run_id } = arret.demarrer();
          return { body: JSON.stringify({ prompt: finalPrompt, resume: sessionId, run_id }), signal }; })(),
      });
      if (!res.ok) {
        let detail = "Erreur au lancement de Claude";
        try {
          const body = await res.json();
          if (body?.detail) detail = body.detail;
        } catch { /* réponse non JSON */ }
        toast.error(detail);
        setRunning(false);
        checkHealth(true);
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          let evt;
          try { evt = JSON.parse(line.slice(6)); } catch { continue; }
          handleEvent(evt);
        }
      }
    } catch (e) {
      if (arret.estArret(e)) pushEvent({ kind: "error", text: "⏹ Arrêté à ta demande" });
      else toast.error("Connexion interrompue");
    } finally {
      setRunning(false);
      checkHealth();
    }
  };

  const handleEvent = (evt) => {
    if (evt.type === "done") { if (evt.session_id) setSessionId(evt.session_id); loadConversations(); return; }
    if (evt.type === "error") { pushEvent({ kind: "error", text: evt.message }); return; }
    if (evt.type === "system") return;
    // messages assistant + actions d'outils
    if (evt.type === "assistant" && evt.message?.content) {
      for (const c of evt.message.content) {
        if (c.type === "text" && c.text) pushEvent({ kind: "assistant", text: c.text });
        if (c.type === "tool_use") pushEvent({ kind: "tool", name: c.name, input: c.input });
      }
    }
    if (evt.type === "result") {
      pushEvent({ kind: "result", text: evt.result || "Terminé",
        meta: `${evt.num_turns || 0} tours · ${((evt.duration_ms || 0) / 1000).toFixed(1)} s` });
      if (evt.session_id) setSessionId(evt.session_id);
    }
  };

  const Tabs = () => (
    <ProjectTabs projectId={projectId} actif="chat" />
  );

  const toolIcon = (name) => {
    if (name === "Read") return <FileText className="w-3.5 h-3.5" />;
    if (name === "Write" || name === "Edit") return <Edit3 className="w-3.5 h-3.5" />;
    return <Terminal className="w-3.5 h-3.5" />;
  };
  const toolLabel = (e) => {
    const t = e.input?.file_path || e.input?.command || e.input?.path || "";
    return `${e.name} ${t}`.trim();
  };

  return (
    <CRMLayout>
      <div className="flex min-h-[calc(100dvh-4rem)] max-w-6xl flex-col p-4 sm:p-6 lg:h-[calc(100vh-4rem)] lg:overflow-hidden lg:p-8">
        <Link to="/dashboard/projects" className="inline-flex items-center gap-2 text-slate-500 hover:text-indigo-600 mb-4">
          <ArrowLeft className="w-4 h-4" /> Retour aux projets
        </Link>
        <Tabs />

        <div className="flex min-h-0 flex-1 flex-col gap-4 lg:flex-row">
          {/* Historique des conversations (gauche) */}
          <div className="flex w-full shrink-0 flex-col lg:w-56">
            <button onClick={newConversation}
              className="w-full mb-2 inline-flex items-center justify-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg px-3 py-2 text-sm font-semibold">
              <Plus className="w-4 h-4" /> Nouveau chat
            </button>
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400 px-1 mb-1.5">Historique</div>
            <div className="max-h-36 flex-1 space-y-1 overflow-y-auto lg:max-h-none">
              {conversations.length === 0 && <div className="text-xs text-slate-400 px-1 py-3">Aucune conversation encore.</div>}
              {conversations.map((c) => (
                <button key={c.session_id} onClick={() => openConversation(c.session_id)}
                  className={`w-full text-left rounded-lg px-2.5 py-2 hover:bg-slate-100 ${sessionId === c.session_id ? "bg-indigo-50 border border-indigo-200" : "border border-transparent"}`}>
                  <div className="text-xs font-medium text-slate-700 line-clamp-2">{c.title}</div>
                  <div className="text-[10px] text-slate-400 mt-0.5">{c.count} msg · {c.updated_at ? new Date(c.updated_at).toLocaleDateString("fr-FR") : ""}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Colonne chat (droite) — zone de dépôt */}
          <div ref={dropRef} className="relative flex min-h-[520px] min-w-0 flex-1 flex-col lg:min-h-0">
            {dragOver && (
              <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-indigo-50/90 border-2 border-dashed border-indigo-500 rounded-xl pointer-events-none">
                <Paperclip className="w-10 h-10 text-indigo-500 mb-2" />
                <span className="text-base font-semibold text-indigo-700">Déposez pour joindre au chat</span>
              </div>
            )}

        {/* Badge santé Claude */}
        <div className="flex items-center gap-2 mb-4">
          {health === null ? (
            <span className="inline-flex items-center gap-1.5 text-xs text-slate-400"><Circle className="w-3 h-3" /> Vérification…</span>
          ) : health.ok ? (
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-full">
              <CheckCircle2 className="w-3.5 h-3.5" /> Claude connecté {health.version ? `· ${health.version}` : ""}
            </span>
          ) : (
            <>
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-600 bg-amber-50 px-2.5 py-1 rounded-full">
                <AlertCircle className="w-3.5 h-3.5" /> {health.detail || "Claude non connecté"}
              </span>
              <Button size="sm" onClick={startLogin} disabled={loginBusy} className="bg-indigo-600 hover:bg-indigo-700 h-7 text-xs">
                {loginBusy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Plug className="w-3.5 h-3.5 mr-1" />}
                Connecter Claude
              </Button>
            </>
          )}
        </div>

        {/* Modale de connexion Claude */}
        {login && (
          <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4" onClick={() => setLogin(null)}>
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-1">
                <h2 className="text-lg font-bold text-slate-900">Connecter votre compte Claude</h2>
                <button onClick={() => setLogin(null)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
              </div>
              <p className="text-xs text-slate-500 mb-4">Connexion via votre abonnement Claude — aucune clé API. À faire une seule fois.</p>

              <ol className="space-y-3 text-sm">
                <li>
                  <div className="font-semibold text-slate-800 mb-1.5">1. Ouvrez la page d'autorisation</div>
                  <div className="flex gap-2">
                    <a href={login.url} target="_blank" rel="noreferrer"
                      className="flex-1 inline-flex items-center justify-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg px-3 py-2 text-sm font-semibold">
                      <ExternalLink className="w-4 h-4" /> Autoriser dans le navigateur
                    </a>
                    <button onClick={() => { navigator.clipboard.writeText(login.url); toast.success("Lien copié"); }}
                      className="border border-slate-200 rounded-lg px-3 hover:bg-slate-50" title="Copier le lien">
                      <Copy className="w-4 h-4 text-slate-500" />
                    </button>
                  </div>
                </li>
                <li>
                  <div className="font-semibold text-slate-800 mb-1.5">2. Collez le code obtenu</div>
                  <input value={code} onChange={(e) => setCode(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && submitLogin()}
                    placeholder="Code d'autorisation…"
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm font-mono" />
                </li>
              </ol>

              <div className="flex justify-end gap-2 mt-5">
                <Button variant="outline" onClick={() => setLogin(null)}>Annuler</Button>
                <Button onClick={submitLogin} disabled={loginBusy || !code.trim()} className="bg-indigo-600 hover:bg-indigo-700">
                  {loginBusy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />} Finaliser
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Flux */}
        <div ref={scrollRef}
          className="flex-1 bg-white border border-slate-200 rounded-xl shadow-sm p-4 overflow-y-auto space-y-3 mb-3"
          style={{ minHeight: 0 }}>
          {events.length === 0 && (
            <div className="h-full flex flex-col items-center justify-center text-slate-400 text-sm py-16">
              <Terminal className="w-8 h-8 mb-3 text-slate-300" />
              Demandez une modification à Claude — il travaille directement dans le dossier du projet.
              <span className="text-xs mt-1 text-slate-300">Glissez-déposez un fichier ou une image ici pour le joindre.</span>
            </div>
          )}
          {events.map((e, i) => {
            if (e.kind === "user") return <div key={i} className="flex justify-end"><div className="bg-indigo-600 text-white text-sm rounded-2xl rounded-br-sm px-3.5 py-2 max-w-[80%]">{e.text}</div></div>;
            if (e.kind === "assistant") return <div key={i} className="text-sm text-slate-700 whitespace-pre-wrap bg-slate-50 rounded-2xl rounded-bl-sm px-3.5 py-2 max-w-[85%]">{e.text}</div>;
            if (e.kind === "tool") return <div key={i} className="inline-flex items-center gap-1.5 text-xs font-mono text-slate-500 bg-slate-100 rounded-md px-2 py-1">{toolIcon(e.name)} {toolLabel(e)}</div>;
            if (e.kind === "error") return <div key={i} className="text-xs text-red-600 bg-red-50 rounded-md px-2.5 py-1.5 inline-flex items-center gap-1.5"><AlertCircle className="w-3.5 h-3.5" /> {e.text}</div>;
            if (e.kind === "result") return <div key={i} className="text-sm text-slate-800 border-l-2 border-emerald-500 bg-emerald-50/50 px-3 py-2 rounded-r-lg"><div className="whitespace-pre-wrap">{e.text}</div><div className="text-[11px] text-slate-400 mt-1">{e.meta}</div></div>;
            return null;
          })}
          {running && <div className="inline-flex items-center gap-1.5 text-xs text-indigo-500"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Claude travaille…</div>}
        </div>

        {/* Actions rapides */}
        <div className="flex flex-wrap gap-1.5 mb-2">
          {QUICK.map((q) => (
            <button key={q} onClick={() => run(q)} disabled={running || !health?.ok}
              className="text-xs border border-slate-200 rounded-full px-3 py-1 text-slate-600 hover:border-indigo-400 hover:text-indigo-600 disabled:opacity-40">
              {q}
            </button>
          ))}
        </div>

        {/* Pièces jointes en attente */}
        {attachments.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-2">
            {attachments.map((a, i) => (
              <span key={i} className="inline-flex items-center gap-1.5 text-xs bg-indigo-50 text-indigo-700 rounded-full px-2.5 py-1">
                <Paperclip className="w-3 h-3" /> {a.name}
                <button onClick={() => setAttachments((arr) => arr.filter((_, k) => k !== i))} className="hover:text-indigo-900"><X className="w-3 h-3" /></button>
              </span>
            ))}
          </div>
        )}

        {/* Saisie */}
        <div className="flex gap-2">
          <input ref={fileRef} type="file" className="hidden" onChange={onPickFile} />
          <button onClick={() => fileRef.current?.click()} disabled={running || !health?.ok}
            title="Joindre un fichier ou une image"
            className="border border-slate-200 rounded-lg px-3 hover:bg-slate-50 disabled:opacity-40">
            <Paperclip className="w-4 h-4 text-slate-500" />
          </button>
          <input value={input} onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && run(input)}
            placeholder={health?.ok ? "Demandez une modification…" : "Connectez Claude sur le serveur pour activer le chat"}
            disabled={running || !health?.ok}
            className="flex-1 border border-slate-200 rounded-lg px-3.5 py-2.5 text-sm disabled:bg-slate-50" />
          {running ? <BoutonArreter onClick={arret.arreter} /> : (
            <Button onClick={() => run(input)} disabled={!health?.ok || (!input.trim() && attachments.length === 0)} className="bg-indigo-600 hover:bg-indigo-700">
              <Send className="w-4 h-4" />
            </Button>
          )}
        </div>
        {sessionId && <div className="text-[11px] text-slate-400 mt-1.5">Session en cours — le contexte est conservé.</div>}
          </div>
        </div>
      </div>
    </CRMLayout>
  );
}
